import { UploadMediaButton } from "@/components";
import { MediaPreview } from "@/components/form/AddMediaForm";
import { uploadMultipleMedia } from "@/utils/uploadMedia";
import { UploadResponse } from "@/utils/uploadToCdn";
import {
  Typography,
  Stack,
  Box,
  IconButton,
  Collapse
} from "@mui/material";
import { TextFieldElement } from "react-hook-form-mui";
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import { ErrorOutline, Sync, CheckCircle, KeyboardArrowRight } from '@mui/icons-material';
import { zodResolver } from "@hookform/resolvers/zod";
import { companyDetailsSchema, ICompanyDetailsFormValues } from "@/utils/zod/generalSettingsFormValidation";
import { useForm } from "@refinedev/react-hook-form";
import { FormProvider } from "react-hook-form";
import { AutoSaveIndicator } from "@refinedev/mui";
import PhoneNumberComponent from "./PhoneNumberComponent";
import { useState } from "react";

const CompanyDetailsSection = ({values}: {values: ICompanyDetailsFormValues}) => {
  
  const methods = useForm<ICompanyDetailsFormValues>({
    refineCoreProps: {
      action: "edit",
      resource: "general-settings",
      id: "company-details",
      queryOptions: {
        enabled: false,
      },
      autoSave: {
        enabled: true,
        debounce: 2000,
        onFinish: (values) => {
          let isValid = true;
        
          values.phoneNumbers.map((phoneNumber: string, index: number) => {
            const cleanedNumber = phoneNumber.replace(/\D/g, '');
        
            if (cleanedNumber.length !== 10) {
              methods.setError(`phoneNumbers.${index}`, {
                type: "manual",
                message: "Please enter a valid 10-digit phone number"
              });
              isValid = false; // Mark as invalid
              return;
            } else {
              methods.clearErrors(`phoneNumbers.${index}`); // Clears error when valid
            }

            if (values.phoneNumbers.some((number: string, i: number) => i !== index && number === phoneNumber)) {
              methods.setError(`phoneNumbers.${index}`, {
                type: "manual",
                message: "Phone number must be unique"
              });
              isValid = false; // Mark as invalid
              return;
            } else {
              methods.clearErrors(`phoneNumbers.${index}`); // Clears error when valid
            }
            return cleanedNumber.length === 10 ? phoneNumber : "";
          });
        
          // Stop auto-save if any phone number is invalid
          if (!isValid) {
            throw new Error("Invalid phone numbers, skipping auto-save.");
          }
        
          return values || null; // Return updated values if all numbers are valid
        }
      }
    },
    resolver: zodResolver(companyDetailsSchema),
    defaultValues: values || {
      logoUrl: null,
      companyName: "",
      godName: "",
      gstin: "",
      website: "",
      addressLine1: "",
      city: "",
      phoneNumbers: [""]
    },
    values: values,
    mode: "all"
  });
  
  const { setValue, watch, register, refineCore: { autoSaveProps , onFinishAutoSave }, getValues } = methods;
  
  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    try {
      const result: UploadResponse[] = await uploadMultipleMedia(files);
      
      setValue("logoUrl", result[0].cdn_url, {shouldDirty: true});
      const values = {...getValues(), logoUrl: result[0].cdn_url};
      onFinishAutoSave(values);
      
    } catch (error) {
      console.error('Upload failed:', error);
    }
  };

  const [isExpanded, setIsExpanded] = useState(true);

  return (
    <FormProvider {...methods}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <IconButton onClick={() => setIsExpanded(!isExpanded)}>
          <KeyboardArrowRight 
            sx={{ 
              color: 'primary.main', 
              fontSize: '2rem',
              transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s'
            }} 
          />
        </IconButton>
        <Typography variant="h6" gutterBottom>
          Company Details
        </Typography>
        {isExpanded && <AutoSaveIndicator {...autoSaveProps} elements={{
          success: <Typography variant="body1" sx={{ color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}><CheckCircle /> Saved</Typography>,
          error: <Typography variant="body1" sx={{ color: 'error.main', display: 'flex', alignItems: 'center', gap: 1 }}><ErrorOutline /> Error</Typography>,
          loading: <Typography variant="body1" sx={{ color: 'warning.main', display: 'flex', alignItems: 'center', gap: 1 }}><Sync /> Saving...</Typography>,
          idle: <Typography variant="body1" sx={{ color: 'grey.500', display: 'flex', alignItems: 'center', gap: 1 }}>Waiting for changes ...</Typography>
        }} />}
      </Stack>

      <Collapse in={isExpanded} sx={{ paddingLeft: '2rem' }}>
        <Stack direction="column" spacing={2}>
          <Stack direction="row" spacing={2} justifyContent="center" alignItems="center">
            <Stack direction="column" spacing={2} width="fit-content">
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: 180, 
              width: 180, 
              border: '1px solid', 
              borderColor: 'divider', 
              borderRadius: 1, 
              overflow: 'hidden',
              position: 'relative',
              '&:hover .upload-button': {
                opacity: 1
              },
              '&:hover .icon': {
                opacity: 0
              }
            }}>
              {watch("logoUrl") ? (
                <MediaPreview 
                  {...register("logoUrl")}
                  media={watch("logoUrl")} 
                  index={1} 
                  width={240} 
                  height={240} 
                />
              ) : (
                <Stack direction="column" spacing={1} justifyContent="center" alignItems="center" className="icon" sx={{ opacity: 1 }}>
                  <InsertPhotoOutlinedIcon color="action" sx={{ fontSize: 32, mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">Upload Logo</Typography>
                </Stack>
              )}
              <Box
                className="upload-button"
                sx={{
                  position: 'absolute',
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  width: '100%',
                  height: '100%',
                }}
              >
                <UploadMediaButton 
                  onInputChange={handleUpload} 
                  size="small"
                  sx={{ 
                    color: 'white',
                    height: '100%'
                  }} 
                />
              </Box>
            </Box>
          </Stack>
        </Stack>

        <Stack direction="row" spacing={2}>
          <TextFieldElement
            name="companyName"
            label="Company Name"
            fullWidth
            size="small"
          />

          <TextFieldElement
            name="godName"
            label="God Name"
            fullWidth
            size="small"
          />
        </Stack>


        <Stack direction="row" spacing={2}>
          <TextFieldElement
            name="gstin"
            label="GSTIN"
            fullWidth
            rules={{
              required: "GSTIN is required",
              pattern: {
                value: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                message: "Please enter a valid GSTIN"
              }
            }}
            size="small"
          />

          <TextFieldElement
            name="website"
            label="Website"
            fullWidth
            rules={{
              pattern: {
                value: /^(https?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(\/\S*)?$/,
                message: "Please enter a valid website URL"
              }
            }}
            size="small"
          />
        </Stack>
        <Stack direction="row" spacing={2}>
          <TextFieldElement
            name="addressLine1"
            label="Address"
            fullWidth
            size="small"
          />

          <TextFieldElement
            name="city"
            label="City"
            fullWidth
            size="small"
          />
        </Stack>

        <PhoneNumberComponent onSave={(values: ICompanyDetailsFormValues) => {
          onFinishAutoSave(values);
        }} />
      </Stack>
      </Collapse>
    </FormProvider>
  );
};

export default CompanyDetailsSection; 